// ============================================================================
// CHECKOUT INVOICES (PMS Phase 2 slice 7a)
// ============================================================================
// crs.Core.DTOs.BillingModule.InvoiceDtos. The invoice adds up the posted folio
// lines and their frozen tax — one per payer, never recomputed.

export const INVOICE_ACTIONS = {
  view: 'view', issue: 'issue', export: 'export',
  // Credit and debit notes (slice 7b)
  creditNote: 'credit-note', debitNote: 'debit-note', approveNote: 'approve-note',
} as const;

/** pms.tbl_invoice.document_kind (slice 7b). */
export type DocumentKind = 'INVOICE' | 'CREDIT_NOTE' | 'DEBIT_NOTE';
export const DOCUMENT_KIND_LABEL: Record<string, string> = {
  INVOICE: 'Invoice', CREDIT_NOTE: 'Credit note', DEBIT_NOTE: 'Debit note',
};

/** A credit (default) or debit note against one invoice. */
export interface NoteRequest {
  /** C (credit, the default) or D (debit, chosen explicitly). */
  direction: 'C' | 'D';
  /** FULL (credit and re-issue), LINES or AMOUNT. */
  kind: 'FULL' | 'LINES' | 'AMOUNT';
  lines: Array<{ lineNo: number; amount?: number | null }>;
  amount?: number | null;
  reason?: string | null;
  confirmEarlierYear: boolean;
}

export interface NotePreview {
  note: Invoice;
  originalInvoiceId: string;
  originalInvoiceNumber: string;
  originalInvoiceDate: string;
  remainingAmount: number;
  earlierYear: boolean;
  needsApproval: boolean;
  approvalLimit: number;
  blockers: string[];
  warnings: string[];
  canIssue: boolean;
}
export const INVOICE_MODULE = 'pms-invoices';

export const PAYER_LABEL: Record<string, string> = {
  GUEST: 'Guest', CORPORATE: 'Corporate', OTA: 'OTA', AGENT: 'Travel agent',
};

export interface InvoiceTax {
  componentCode: string;
  componentName?: string | null;
  ratePercentage: number;
  taxableBase: number;
  taxAmount: number;
}

export interface InvoiceLine {
  lineNo: number;
  chargeCategory: string;
  description: string;
  sacHsnCode?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  quantity: number;
  unitRate: number;
  taxableAmount: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  isTaxInclusive: boolean;
  /** Paid on the guest's behalf at cost (slice 7c): listed apart, outside the taxable value. */
  isReimbursement?: boolean;
  taxes: InvoiceTax[];
}

export interface Invoice {
  id: string;
  propertyId: string;
  bookingId: string;
  folioId: string;
  bookingNumber?: string | null;
  folioNumber?: string | null;
  /** Null on a preview. */
  invoiceNumber?: string | null;
  invoiceDate: string;
  documentTitle: 'TAX_INVOICE' | 'INVOICE' | string;
  billToType: string;
  billToId?: string | null;
  billToName: string;
  billToGstin?: string | null;
  billToStateCode?: string | null;
  billToAddress?: string | null;
  isB2b: boolean;
  placeOfSupplyCode?: string | null;
  guestName?: string | null;
  stayFrom?: string | null;
  stayTo?: string | null;
  roomNumbers?: string | null;
  taxableAmount: number;
  taxAmount: number;
  /** Disbursements recovered at cost (slice 7c). */
  reimbursementAmount?: number;
  roundOff: number;
  totalAmount: number;
  invoiceStatus: string;
  issuedAt?: string | null;
  issuedByName?: string | null;
  documentKind?: DocumentKind;
  originalInvoiceId?: string | null;
  originalInvoiceNumber?: string | null;
  originalInvoiceDate?: string | null;
  noteKind?: string | null;
  noteReason?: string | null;
  approvedByName?: string | null;
  creditedAmount?: number;
  debitedAmount?: number;
  lines: InvoiceLine[];
  taxSummary: InvoiceTax[];
}

export interface InvoiceListRow {
  id: string;
  propertyId: string;
  bookingId: string;
  bookingNumber?: string | null;
  invoiceNumber: string;
  invoiceDate: string;
  documentTitle: string;
  billToType: string;
  billToName: string;
  billToGstin?: string | null;
  guestName?: string | null;
  roomNumbers?: string | null;
  taxableAmount: number;
  taxAmount: number;
  totalAmount: number;
  invoiceStatus: string;
  /** INVOICE / CREDIT_NOTE / DEBIT_NOTE (slice 7b). */
  documentKind?: DocumentKind;
  originalInvoiceNumber?: string | null;
  creditedAmount?: number;
  debitedAmount?: number;
  createdByName?: string | null;
  totalCount: number;
}

export interface InvoicePreview {
  bookingId: string;
  bookingNumber?: string | null;
  folioId?: string | null;
  folioNumber?: string | null;
  guestName?: string | null;
  bookingStatusCode?: string | null;
  invoiceDate: string;
  invoices: Invoice[];
  issued: InvoiceListRow[];
  blockers: string[];
  canIssue: boolean;
  /** Lines billed to a payer the stay's settlement no longer names (2026-10-16) — a warning. */
  misbilledToParty?: number;
  misbilledToPartyAmount?: number;
  misbilledToGuest?: number;
  misbilledToGuestAmount?: number;
  settlementMode?: string | null;
  settlementFollowsParty?: boolean;
  settlementPartyName?: string | null;
  /** Night lines of departed rooms not posted yet (slice 7a-2) — Post remaining nights. */
  unpostedNightLines?: number;
  unpostedNightAmount?: number;
  /** What the guest still owes (negative = credit). */
  guestBalance: number;
}

export interface IssueInvoicesResult {
  invoices: InvoiceListRow[];
  folioClosed: boolean;
  guestBalance: number;
}

export interface StayToInvoice {
  bookingId: string;
  bookingNumber: string;
  folioId: string;
  folioNumber?: string | null;
  guestName?: string | null;
  checkInDate: string;
  checkOutDate: string;
  roomNumbers?: string | null;
  openLines: number;
  openAmount: number;
  guestBalance: number;
}

export interface InvoiceListQuery {
  propertyId: string;
  from?: string;
  to?: string;
  billToType?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}
